import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useRef,
} from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./auth";

interface SocketContextType {
	socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({ socket: null });

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const { user } = useAuth();
	const socketRef = useRef<Socket | null>(null);

	useEffect(() => {
		if (!user?.id) return;

		const url = process.env.REACT_APP_HOSTNAME || "http://localhost:8585";

		socketRef.current = io(url, {
			withCredentials: true,
			transports: ["websocket"],
			auth: {
				token: localStorage.getItem("token"),
			},
			reconnection: true, // Enable auto-reconnect (true by default, but good to be explicit)
			reconnectionAttempts: Infinity, // Never give up trying to reconnect
			reconnectionDelay: 1000, // Wait 1 second before first retry
			reconnectionDelayMax: 5000, // Never wait more than 5 seconds between retries
			randomizationFactor: 0.5, // Adds jitter (0.5 to 1.5 seconds) so 30 admins don't reconnect at the exact same millisecond
		});

		socketRef.current.on("connect_error", (err) => {
			console.log("Connection Failed:", err.message); // e.g., "Token is expired"

			if (
				err.message === "Token is expired" ||
				err.message === "Token is invalid"
			) {
				// Log the user out, clear local storage, redirect to login, etc.
				console.log(`Auth socket error: ${err.message}, Logging user out`);
			}
		});

		socketRef.current.on("connect", () => {
			console.log("Main App Socket connected");
		});

		socketRef.current.on("disconnect", (reason) => {
			// If the server forced it closed, or if network was lost
			if (reason === "io server disconnect") {
				// Only reconnect manually if the server explicitly kicked them
				socketRef.current?.connect();
			}
			// Otherwise, the socket will automatically try to reconnect in the background!
		});

		setSocket(socketRef.current);

		return () => {
			if (socketRef.current) {
				socketRef.current.disconnect();
				socketRef.current = null;
				setSocket(null);
			}
		};
	}, [user?.id]);

	return (
		<SocketContext.Provider value={{ socket }}>
			{children}
		</SocketContext.Provider>
	);
};

export const useSocket = () => useContext(SocketContext);
